// Zentrale Konstanten. Backend-Spiegel: functions/shared/constants.js -
// beide Dateien synchron halten, die Server-Validierung prüft gegen genau
// diese Werte.

export const MEMBER_STATUS = {
  PENDING: 'pending',
  ACTIVE: 'active',
  REJECTED: 'rejected',
  BLOCKED: 'blocked',
};

export const ALL_MEMBER_STATUSES = Object.values(MEMBER_STATUS);

export const MEMBER_STATUS_LABELS = {
  pending: 'Wartet auf Freigabe',
  active: 'Aktives Mitglied',
  rejected: 'Abgelehnt',
  blocked: 'Gesperrt',
};

export const EVENT_VISIBILITY = {
  PUBLIC: 'public',
  MEMBERS: 'members',
};

export const EVENT_VISIBILITY_LABELS = {
  public: 'Öffentlich',
  members: 'Nur Mitglieder',
};

/**
 * Link am Termin (Tickets, Anmeldung, Veranstaltungsseite). Muss zur
 * Längenprüfung in firestore.rules passen; erlaubt sind dort nur http und
 * https — siehe utils/links.js.
 */
export const EVENT_LINK_MAX_LENGTH = 300;

/**
 * Veranstalter eines Termins — „Mo's Bikertreff", „nrw_autocrew".
 *
 * NICHT zu verwechseln mit `createdBy` (das Clubmitglied, das den Termin
 * eingetragen hat) und nicht mit der Rolle `isOrganizer`. Deshalb heißt das
 * Feld `organizedBy` und nicht `organizer`: Neben isOrganizer() in
 * firestore.rules wäre der kürzere Name beim Lesen verwechselbar.
 *
 * Stand bis v0.9.7 in Klammern im Titel („MINI Saisonende 2026 (organisiert
 * von Mo's Bikertreff)") — die Überschrift brach dadurch über vier Zeilen.
 * Muss zur Längenprüfung in firestore.rules passen.
 */
export const EVENT_ORGANIZED_BY_MAX_LENGTH = 120;

/**
 * Antwort auf eine Einladung. Bewusst KEIN Spiegel in
 * functions/shared/constants.js: Durchgesetzt wird die Liste in
 * firestore.rules (geschrieben wird direkt vom Client), ein Backend-Spiegel
 * wäre toter Code. Wer hier etwas ergänzt, muss die Rule mit ändern.
 */
export const EVENT_RESPONSE = {
  YES: 'yes',
  MAYBE: 'maybe',
  NO: 'no',
};

export const ALL_EVENT_RESPONSES = Object.values(EVENT_RESPONSE);

export const EVENT_RESPONSE_LABELS = {
  yes: 'Ich bin dabei',
  maybe: 'Vielleicht',
  no: 'Diesmal nicht',
};

/** Kurzform für Listenüberschriften und Zähler. */
export const EVENT_RESPONSE_SHORT = {
  yes: 'Zusagen',
  maybe: 'Vielleicht',
  no: 'Absagen',
};

/**
 * Beschriftung der Knöpfe auf einer Terminkarte. Bewusst nicht
 * EVENT_RESPONSE_SHORT: Das sind Überschriften über Namenslisten („Zusagen"),
 * hier steht eine Handlung auf dem Knopf. Der volle Satz aus
 * EVENT_RESPONSE_LABELS bleibt als aria-label erhalten.
 */
export const EVENT_RESPONSE_COMPACT = {
  yes: 'Dabei',
  maybe: 'Vielleicht',
  no: 'Absagen',
};

export const EVENT_RESPONSE_ICONS = {
  yes: '✅',
  maybe: '🤔',
  no: '❌',
};

/** Muss zur Längenprüfung in firestore.rules passen. */
export const COMMENT_MAX_LENGTH = 1000;

/**
 * Aushang-Brett. Beide Werte müssen zur Längenprüfung in firestore.rules
 * passen — geschrieben wird direkt vom Client, die Rule ist die einzige
 * Instanz, die das durchsetzt.
 */
export const ANNOUNCEMENT_TITLE_MAX_LENGTH = 120;
export const ANNOUNCEMENT_MAX_LENGTH = 2000;

/**
 * „Schreib uns was zu deinem Auto" - der Freitext im eigenen Profil.
 *
 * Spiegel von MAX_LENGTHS.carStory in functions/shared/constants.js. Anders
 * als beim Aushang ist die Server-Seite hier die durchsetzende Instanz
 * (gespeichert wird über updateMemberProfile, nicht direkt vom Client); diese
 * Zahl steuert nur das maxLength des Feldes und den Zeichenzähler darunter.
 * Sie muss trotzdem stimmen: Der Server kappt stillschweigend, und ein
 * Zeichenzähler, der mehr erlaubt, als ankommt, wäre eine Lüge.
 */
export const CAR_STORY_MAX_LENGTH = 1500;

/**
 * Spitzname eines Fahrzeugs — Spiegel von `MAX_LENGTHS.vehicleNickname`.
 *
 * Kurz gehalten, weil er als Beschriftung am Umschalter und auf der
 * Crew-Kachel steht — beides Orte, an denen Breite das knappste Gut ist.
 */
export const VEHICLE_NICKNAME_MAX_LENGTH = 40;

/**
 * Info-Nachricht des Teams an ein Mitglied - Spiegel von
 * MAX_LENGTHS.noticeTitle / .noticeText.
 */
export const NOTICE_TITLE_MAX_LENGTH = 120;
export const NOTICE_TEXT_MAX_LENGTH = 2000;

/**
 * Wie oft eine Info-Nachricht angezeigt wird, bevor sie verschwindet.
 * Spiegel von NOTICE_MAX_VIEWS in functions/shared/notices.js.
 */
export const NOTICE_MAX_VIEWS = 2;

/**
 * Die ausgelieferte Version - Admins sehen sie im Header.
 *
 * Wozu: Ohne CI gibt es keine Stelle, an der man sieht, ob ein Deploy
 * durch ist. Die Zahl kommt mit dem Bundle, zeigt also genau das, was
 * gerade im Browser läuft.
 *
 * Bewusst eine eigene Konstante und nicht der oberste Eintrag aus
 * `releaseNotes.js`: Dort steht nicht jede Version (v0.34.0 fehlt), die
 * Anzeige liefe hinterher. `appVersion.test.js` prüft, dass sie zur
 * obersten Version in RELEASENOTES.md passt - neue Version dort = hier
 * hochzählen, sonst wird der Test rot.
 */
export const APP_VERSION = 'v0.42.0';

/**
 * Club-Stammdaten an einer Stelle - tauchen in Header, Footer, Impressum,
 * Kontakt und den Meta-Tags auf.
 *
 * Name und Claim stammen vom bestehenden Auftritt (Logo/Instagram), damit
 * Website und Instagram als derselbe Club erkennbar sind.
 */
export const CLUB = {
  name: 'Club Mini-Thrills',
  claim: 'Weil Leidenschaft verbindet!',
  // Gruendungsjahr. Stand bis v0.8.11 auf 2024 und war falsch - korrigiert
  // von Christian am 07.09.2026. Es speist die Untergrenze von "Dabei seit"
  // (MEMBER_SINCE_FROM in utils/membership.js): Frueher als hier kann niemand
  // dabei gewesen sein. Backend-Spiegel: CLUB_SINCE in
  // functions/shared/constants.js - beide muessen zusammenpassen.
  since: 2022,
  // Erscheint im Hero neben dem Clubnamen. NICHT die Anschrift aus dem
  // Impressum - die ist die ladungsfaehige Adresse des Verantwortlichen und
  // steht bewusst getrennt in Impressum.js und Privacy.js.
  //
  // Bis v0.19.1 „Bonn/Sieg und Umgebung". Seit v0.20.0 ist der Club offen
  // fuer Orgas in ganz Deutschland - Bonn/Sieg bleibt als Herkunft stehen,
  // nicht als Grenze. Weiterhin EIN Club, keine Regionalgruppen.
  region: 'Gegründet in Bonn/Sieg',
  // Postfach angelegt am 07.09.2026. Die Adresse steht im Impressum, im
  // Kontaktformular, auf den Statusseiten und in der Datenschutzerklärung -
  // wer sie hier ändert, ändert sie an all diesen Stellen mit.
  email: 'info@clubminithrills.de',
  instagramHandle: '@clubminithrills',
  instagram: 'https://www.instagram.com/clubminithrills/',
};

/**
 * Ab wann ein Termin als gemeinsame Club-Aktion gilt.
 *
 * Christians Regel lautet „ab 4 Zusagen" (bis v0.9.11: „mehr als 4
 * Interessierte", also ab 5).
 *
 * Die Zahl steht hier und nicht im Text, damit Schwelle und Formulierung nicht
 * auseinanderlaufen - CLUB_ACTION_NOTE und der Hinweis im EventEditor setzen
 * sie ein. Genau das war bis v0.9.11 nur die halbe Wahrheit: `full` und der
 * Editor-Hinweis trugen die 5 als Festtext und hätten beim Absenken stehen
 * bleiben können.
 */
export const CLUB_ACTION_MIN_YES = 4;

/**
 * Der Club-Aktions-Hinweis an EINER Stelle.
 *
 * Stand bis v0.9.7 wortwörtlich in der Beschreibung jedes einzelnen Termins -
 * auf /termine dreimal untereinander, wodurch die eigentlichen Infos des
 * Veranstalters aus dem Blick gerieten. Gleiche Rolle wie TRADEMARK_NOTE: ein
 * Clubtext, der an mehreren Stellen wörtlich gleich erscheinen muss.
 *
 * Drei Teile, damit die öffentliche Terminseite die Kurzform und die
 * Detailseite den vollen Text zeigen kann, ohne dass zwei Fassungen
 * entstehen, die getrennt gepflegt werden müssten.
 */
export const CLUB_ACTION_NOTE = {
  short:
    `Ab ${CLUB_ACTION_MIN_YES} Zusagen wird aus einem Termin eine gemeinsame Club-Aktion: ` +
    'Wir fahren dann in Kolonne, stellen uns zusammen auf und zeigen Flagge.',
  full:
    `Wir werten ein Event als gemeinsame Club-Aktion, wenn mindestens ${CLUB_ACTION_MIN_YES} ` +
    'Interessierte auf den Button „Dabei" geklickt haben. ' +
    '(Das bedeutet für uns: Wir versuchen in Kolonne zu fahren, ' +
    'uns zusammen aufzustellen, Visitenkarten auszulegen oder auch einen Banner aufzuhängen.)',
  chat:
    'Stimmt euch hierzu gerne im kleinen Chatfenster darunter selbst ab – z. B. bzgl. ' +
    'Treffpunkt, Anfahrt, Mitfahrgelegenheit, usw.',
};

// Markenrechtlicher Hinweis - MINI ist eine Marke der BMW AG. Diese Seite ist
// ein privates Fan-Projekt und darf nicht offiziell wirken.
export const TRADEMARK_NOTE =
  `${CLUB.name} ist ein privates Fan-Projekt von Enthusiasten. Keine Verbindung zur BMW AG, ` +
  'zu MINI oder einem offiziellen Händler. Alle genannten Marken gehören ihren jeweiligen Inhabern.';

export const MAX_IMAGE_BYTES = 5 * 1024 * 1024;
export const ACCEPTED_IMAGE_TYPES = ['image/jpeg', 'image/png', 'image/webp'];

/**
 * Profilbilder: Kantenlänge, auf die der Browser vor dem Hochladen
 * verkleinert, und die Größengrenze der fertigen Datei.
 *
 * Ein Handyfoto wiegt 8 MB und ist 4000px breit — als Banner ausgeliefert
 * würde die Crew-Liste bei 20 Mitgliedern dreistellige Megabyte nachladen.
 * 2400px sind auf jedem Bildschirm scharf, das Ergebnis wiegt ~300 KB.
 * `maxBytes` spiegelt die Storage-Rule; die Prüfung im Browser ersetzt sie
 * nicht, sie erspart nur den vergeblichen Upload.
 */
export const IMAGE_LIMITS = {
  // `aspect` ist zugleich das Seitenverhältnis des Zuschnitt-Rahmens und das
  // der Anzeige — muss zu `aspect-ratio` von `.mt-profile__banner` bzw. zum
  // runden Profilbild in `member.css` passen, sonst schneidet die Anzeige
  // etwas ab, das der Nutzer im Editor bewusst hineingezogen hat.
  avatar: { maxEdge: 512, maxBytes: 2 * 1024 * 1024, aspect: 1 },
  banner: { maxEdge: 2400, maxBytes: 8 * 1024 * 1024, aspect: 16 / 5 },
  // Titelbild eines Termins. `aspect: 4/3` muss zu .mt-event__cover img in
  // App.css passen - sonst schneidet die Karte weg, was der Organisator im
  // Zuschnitt-Editor bewusst hineingezogen hat.
  eventCover: { maxEdge: 1600, maxBytes: 4 * 1024 * 1024, aspect: 4 / 3 },
};

/**
 * Weitere Bilder innerhalb eines Termins.
 *
 * Bewusst OHNE `aspect` und damit ohne Zuschnitt-Editor: Das sind Fotos vom
 * Treffen in ganz unterschiedlichen Formaten, ein erzwungener 4:3-Rahmen
 * schnitte reihenweise Autos an. Verkleinert wird trotzdem - ein 8-MB-Handyfoto
 * unverändert auszuliefern wäre bei zwölf Bildern pro Termin unzumutbar.
 */
export const EVENT_PHOTO_MAX_EDGE = 1600;

/**
 * Galeriefotos.
 *
 * ⚠️ Diese Konstante ist die Antwort auf einen echten Befund (Messung
 * 13./14.09.2026): Der Galerie-Upload war der EINZIGE Weg im Projekt ohne
 * Verkleinerung - `GalleryManager` schob die Datei roh in den Storage. Dadurch
 * lagen 104 Fotos mit zusammen **108,7 MB** dort, im Schnitt rund 1 MB je
 * Bild, und wurden auf der Startseite als **269 px breite** Vorschau
 * ausgeliefert. Zum Vergleich: ein Terminbild derselben Art wiegt nach
 * `cropImage` 154 KB.
 *
 * Kein `aspect` und damit kein Zuschnitt-Editor - gleiche Begründung wie bei
 * den Terminfotos: Das sind Bilder vom Treffen in allen Formaten, ein
 * erzwungener Rahmen schnitte reihenweise Autos an.
 *
 * 1600 wie beim Terminbild und beim Bingo: Es ist zugleich die Größe für die
 * Vollbildansicht in der Galerie, nicht nur für die Kachel.
 */
export const GALLERY_PHOTO_MAX_EDGE = 1600;
export const EVENT_PHOTOS_MAX = 12;

/** Bildunterschrift eines Terminfotos - dient zugleich als Alternativtext. */
export const EVENT_PHOTO_CAPTION_MAX = 140;

// Öffentlicher Pfad der Kartengeometrie. Erzeugt von tools/build-plz-map.mjs.
export const PLZ_GEOMETRY_URL = `${process.env.PUBLIC_URL || ''}/geo/plz3.json`;
